import React from 'react';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';

const EFFECTIVE_DATE = '4 March 2026';
const COMPANY = 'Best Business Development';
const CONTACT_EMAIL = 'info@bbdoutreachengine.com';
const SITE = 'bbdoutreachengine.com';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>
    <div className="space-y-3 text-gray-600 leading-relaxed text-sm">{children}</div>
  </div>
);

const TermsPage = () => (
  <div className="min-h-screen bg-white" style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

    {/* NAV */}
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-md shadow-indigo-200">
              <Zap size={16} className="text-white" />
            </div>
            <span className="text-lg font-bold text-gray-900 tracking-tight">Outreach Engine</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">Sign In</Link>
            <Link to="/pricing" className="bg-indigo-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
              Get Started
            </Link>
          </div>
        </div>
      </div>
    </header>

    {/* CONTENT */}
    <main className="max-w-3xl mx-auto px-6 py-16">
      <div className="mb-12">
        <p className="text-indigo-600 text-xs font-bold uppercase tracking-widest mb-3">Legal</p>
        <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight mb-3">Terms of Service</h1>
        <p className="text-sm text-gray-400">Effective date: {EFFECTIVE_DATE}</p>
      </div>

      <p className="text-gray-600 text-sm leading-relaxed mb-10">
        These Terms of Service ("Terms") govern your access to and use of Outreach Engine, a software-as-a-service
        platform operated by <strong>{COMPANY}</strong> ("we", "us", or "our"), a company registered in the United
        Kingdom. By subscribing to or using Outreach Engine, you agree to be bound by these Terms.
        If you do not agree, do not use the service.
      </p>

      <Section title="1. The Service">
        <p>
          Outreach Engine provides tools for cold email campaigns, SMS outreach, email verification, prospect
          management, and related analytics (the "Service"). Access is provided on a subscription basis
          following successful payment through our payment processor, ThriveCart.
        </p>
        <p>
          The Service requires you to connect your own third-party API credentials (e.g. SMTP server, Twilio
          account, Resend API key). We do not provide email or SMS sending infrastructure directly. You are
          responsible for ensuring your use of those third-party services complies with their respective terms.
        </p>
      </Section>

      <Section title="2. Account Registration">
        <p>
          Account creation requires prior payment. After a successful purchase, you will receive an email
          containing a link to set your password and activate your account. This link is valid for 48 hours.
        </p>
        <p>
          You must provide accurate information and keep your account credentials secure. You are responsible
          for all activity that occurs under your account. Notify us immediately at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a>{' '}
          if you suspect unauthorised access.
        </p>
        <p>
          One account corresponds to one subscription. You may not share access credentials with others outside
          your organisation.
        </p>
      </Section>

      <Section title="3. Subscriptions and Billing">
        <p>
          Subscriptions are billed monthly or annually in advance through ThriveCart. Prices are as displayed
          on our pricing page at the time of purchase. We reserve the right to change pricing with reasonable
          notice.
        </p>
        <p>
          Recurring payments are processed automatically on each renewal date. You are responsible for keeping
          your payment details up to date in ThriveCart. If a payment fails and cannot be resolved, we may
          suspend or terminate your account.
        </p>
        <p>
          <strong>30-day money-back guarantee.</strong> If you are not satisfied with Outreach Engine, you may
          request a full refund within 30 days of your initial purchase by contacting us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a>.
          After 30 days, payments are non-refundable. Refunds for top-up credit purchases are not available
          once credits have been used.
        </p>
        <p>
          To cancel your subscription, do so directly in ThriveCart. You will retain access until the end of
          your current billing period. Cancellation does not entitle you to a refund unless within the 30-day
          window described above.
        </p>
      </Section>

      <Section title="4. Acceptable Use">
        <p>You agree not to use the Service to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Send spam, unsolicited bulk messages, or communications that violate applicable law (including CAN-SPAM, GDPR, or PECR);</li>
          <li>Harass, threaten, or harm any individual or organisation;</li>
          <li>Upload, transmit, or store unlawfully obtained personal data;</li>
          <li>Attempt to gain unauthorised access to the Service or its underlying infrastructure;</li>
          <li>Reverse-engineer, copy, or create derivative works of the Service;</li>
          <li>Resell or sublicense access to the Service without our written permission.</li>
        </ul>
        <p>
          We reserve the right to suspend or terminate accounts that violate these restrictions without notice
          or refund.
        </p>
      </Section>

      <Section title="5. Your Data and Content">
        <p>
          You retain ownership of all prospect data, email content, and other materials you upload to the
          Service ("Your Content"). By uploading content, you grant us a limited licence to process it solely
          for the purpose of providing the Service to you.
        </p>
        <p>
          You are the data controller for any personal data relating to your prospects. We act as a data
          processor on your behalf. You are responsible for ensuring you have a lawful basis to contact
          prospects and that your campaigns comply with applicable data protection laws.
        </p>
        <p>
          We do not sell Your Content or use it for any purpose other than operating the Service.
        </p>
      </Section>

      <Section title="6. Third-Party Services">
        <p>
          The Service integrates with third-party providers including ThriveCart (payments), Twilio (SMS),
          Resend and SendGrid (email delivery), and Hunter.io (email verification). Your use of these services
          is subject to their own terms and privacy policies. We are not responsible for their actions or
          availability.
        </p>
      </Section>

      <Section title="7. Intellectual Property">
        <p>
          All software, design, trademarks, and content comprising the Outreach Engine platform (excluding
          Your Content) are the intellectual property of {COMPANY}. Nothing in these Terms transfers any
          intellectual property rights to you beyond the limited right to use the Service as described herein.
        </p>
      </Section>

      <Section title="8. Availability and Modifications">
        <p>
          We aim to maintain high availability but do not guarantee uninterrupted access. We may perform
          maintenance, introduce new features, or modify or discontinue features at any time. We will give
          reasonable notice of significant changes where possible.
        </p>
      </Section>

      <Section title="9. Disclaimers and Limitation of Liability">
        <p>
          The Service is provided "as is" and "as available" without warranties of any kind, express or
          implied, including warranties of merchantability or fitness for a particular purpose.
        </p>
        <p>
          To the fullest extent permitted by law, {COMPANY} shall not be liable for any indirect, incidental,
          special, consequential, or punitive damages, or any loss of profits, revenue, data, or business
          opportunities, arising from your use of or inability to use the Service.
        </p>
        <p>
          Our total aggregate liability to you for any claim arising under these Terms shall not exceed the
          total subscription fees paid by you in the 3 months preceding the claim.
        </p>
        <p>
          Nothing in these Terms limits liability for fraud, death, or personal injury caused by our
          negligence, or any other liability that cannot be excluded under English law.
        </p>
      </Section>

      <Section title="10. Termination">
        <p>
          Either party may terminate at any time. You may cancel via ThriveCart; we may terminate your account
          if you breach these Terms or if we discontinue the Service. Upon termination, your right to access
          the Service ceases immediately. We will retain your data for up to 30 days after termination to
          allow for data export, after which it may be deleted.
        </p>
      </Section>

      <Section title="11. Governing Law">
        <p>
          These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the
          exclusive jurisdiction of the courts of England and Wales.
        </p>
      </Section>

      <Section title="12. Changes to These Terms">
        <p>
          We may update these Terms from time to time. We will notify you by email or via the Service at
          least 14 days before material changes take effect. Continued use of the Service after changes take
          effect constitutes acceptance of the updated Terms.
        </p>
      </Section>

      <Section title="13. Contact">
        <p>
          Questions about these Terms should be directed to:{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a>
        </p>
      </Section>
    </main>

    {/* FOOTER */}
    <footer className="bg-gray-950 border-t border-gray-800 py-8 px-6">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
            <Zap size={14} className="text-white" />
          </div>
          <span className="text-white font-bold text-sm">Outreach Engine</span>
        </Link>
        <div className="flex items-center gap-8">
          <Link to="/" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Home</Link>
          <Link to="/pricing" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Pricing</Link>
          <Link to="/terms" className="text-gray-400 hover:text-gray-300 text-sm transition-colors">Terms</Link>
          <Link to="/privacy" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Privacy</Link>
        </div>
        <p className="text-gray-600 text-sm">© {new Date().getFullYear()} {COMPANY}</p>
      </div>
    </footer>
  </div>
);

export default TermsPage;
